<template>
  <div
    class="flex flex-col gap-1 col-span-12 @sm:col-[span_var(--col-sm)] @md:col-[span_var(--col-md)] @lg:col-[span_var(--col-lg)] @xl:col-[span_var(--col-xl)] @2xl:col-[span_var(--col-xxl)]"
    :style="{
      '--col-sm': colSpan.sm,
      '--col-md': colSpan.md,
      '--col-lg': colSpan.lg,
      '--col-xl': colSpan.xl,
      '--col-xxl': colSpan.xxl,
    }"
  >
    <slot name="label" :star="showStar" :label="label">
      <label class="flex flex-row gap-0.5 items-top label" :for="props.name">
        {{ label }}
        <p class="font-bold text-[#FF3C5F] text-[14px]" v-if="showStar">*</p>
        <slot
          name="labelAdjacent"
          v-if="$slots.labelAdjacent"
          :star="showStar"
          :label="label"
        ></slot>
      </label>
    </slot>
    <div class="flex flex-row items-center gap-1">
      <FormFieldDynamic
        v-model:value="value"
        v-model:form="form"
        v-model:actual="actualValue"
        :dataKey="props.dataKey"
        :affectFields="props.affectFields"
        :context="props.context"
        :showAllOptionInSelect="props.showAllOptionInSelect"
        :disabled="props.disabled"
        :inputType="props.inputType"
        :auxInputType="props.auxInputType"
        :accepts="accepts"
        @change="changeEventHandler($event)"
        @input="inputEventHandler($event)"
        @blur="blurEventHandler($event)"
        @multiSelectAllChange="multiSelectAllChangeEventHandler($event)"
        @onEnterPressed="
          () => {
            emits('onEnterPressed', {});
          }
        "
        v-bind="attrs"
        :loading="props.loading"
        :misc="props.misc"
        :errors="props.errors?.join('. ')"
        :id="id"
        :placeholder="props.placeholder"
        :name="props.name"
        :min="props.min"
        :max="props.max"
        :tableName="props.tableName"
        :required="props.required"
        :defaultExpression="props.defaultExpression"
        :searchType="props.searchType"
        class="w-full"
      />
      <slot name="inputAdjacent"></slot>
    </div>
    <slot name="info" :error="error">
      <div class="text-red-700 text-[0.625rem] h-[1rem]">
        {{ error }}
      </div>
    </slot>
  </div>
</template>

<script setup lang="ts">
import { InputType } from "~/scad-shared/types/generic";
import {
  type IInputEvent,
  type IChangeEvent,
  type IBlurEvent,
  type IMisc,
} from "~/scad-shared/types/interfaces";

defineSlots<{
  default(props: {}): any;
  label(props: { star: boolean; label?: string }): any;
  labelAdjacent(props: {}): any;
  inputAdjacent(props: {}): any;
  info(props: { error: string }): any;
}>();

const attrs = useAttrs();

interface IColSpans {
  sm: number;
  md: number;
  lg: number;
  xl: number;
  xxl: number;
}

const props = withDefaults(
  defineProps<{
    label?: string;
    validation?: string[] | string | undefined;
    disabled?: boolean;
    loading?: boolean;
    inputType?: InputType;
    auxInputType?: string;
    misc?: IMisc;
    id?: string;
    searchType?: string;
    placeholder?: string;
    colSpan?: IColSpans;
    errors?: string[];
    showStar?: boolean;
    name?: string;
    dataKey?: string;
    affectFields?: string[];
    showAllOptionInSelect?: boolean;
    showErrorMessage?: boolean;
    min?: any;
    max?: any;
    tableId?: number;
    tableName?: string;
    required?: boolean;
    defaultExpression?: string;
    context?: Indexable;
  }>(),
  {
    disabled: false,
    loading: false,
    dataKey: Math.random().toString().substring(2, 5),
    inputType: InputType.InputText,
    showStar: false,
    context: () => ({}),
    showErrorMessage: true,
    affectFields: () => [],
    colSpan: () => ({
      sm: 12,
      md: 6,
      lg: 4,
      xl: 3,
      xxl: 3,
    }),
    tableName: "",
    required: false,
  },
);

const value = defineModel<any>("value");
const actualValue = defineModel<any>("actual");
const form = defineModel<any>("form");
const error: ComputedRef<string> = computed(() => {
  // Add some sort of per-field validation
  return props.errors ? props.errors.join(". ") : "";
});

const emits = defineEmits<{
  change: [event: any];
  input: [event: any];
  blur: [event: any];
  multiSelectAllChange: [event: boolean];
  onEnterPressed: [event: any];
}>();

const accepts = computed<string>(() => {
  let accept = "";
  if (props.inputType === InputType.FileUpload) {
    // Check validation rules for accepted file types
    if (props.validation instanceof Array) {
      props.validation.forEach((rule: string) => {
        let filteredRule = rule.trim();
        if (filteredRule.charAt(0) === "[") {
          filteredRule = filteredRule.slice(1);
        }

        if (filteredRule.charAt(filteredRule.length - 1) === "]") {
          filteredRule = filteredRule.slice(0, -1);
        }
        if (filteredRule.startsWith("acceptFileTypes")) {
          const parts = filteredRule.split(",");

          if (parts.length > 1) {
            accept = parts.slice(1).join(",");
          }
        }
      });
    }
    // return ".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png";
  }
  return accept;
});

function changeEventHandler($event: IChangeEvent) {
  emits("change", { ...$event, ...{ value: value.value } });
}
function inputEventHandler($event: IInputEvent) {
  emits("input", { ...$event, ...{ value: value.value } });
}
function blurEventHandler($event: IBlurEvent) {
  emits("blur", { ...$event, ...{ value: value.value } });
}
function multiSelectAllChangeEventHandler($event: boolean) {
  emits("multiSelectAllChange", $event);
}
</script>
